import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Cable,
  Database,
  FlaskConical,
  MessageCircle,
  Plus,
  Minus,
  SlidersHorizontal,
} from "lucide-react";
import AssayPrograms from "../solutions/AssayPrograms";
import ResearchCompanion from "./ResearchCompanion";
import AssayLanguageDemo from "../solutions/AssayLanguageDemo";
import ScientificDataVisual from "../solutions/ScientificDataVisual";
import AgentInfrastructureSection from "../solutions/AgentInfrastructureSection";
import { FieldNotes } from "./HomeSections";
import ScientificEvidence, { ScientificContext } from "./ScientificEvidence";
import { LabHistory, ScientificEvaluation } from "./ScientistDeployment";
import ScientistTeam from "./ScientistTeam";
import ProblemRequests from "./ProblemRequests";
import { COMPANY_WORK, SCIENCE_LINKS, SOLUTION_AREAS, PUBLIC_WORK_ARTIFACT } from "../../content/siteContent";
import "../solutions/Solutions.css";
import "./ScientistStory.css";

const pillarDetails = {
  "lab-automation": {
    tabId: "automation",
    sentence: "Working assays, automated for your lab.",
    icon: FlaskConical,
  },
  "scientific-data": {
    tabId: "data",
    sentence: "Traceable datasets from instrument exports and experiment records.",
    icon: Database,
  },
  "specialized-ai": {
    tabId: "ai",
    sentence: "Specialized agents with the tools, permissions and tests your work requires.",
    icon: SlidersHorizontal,
  },
};

const pillars = SOLUTION_AREAS.map((area) => ({ ...area, ...pillarDetails[area.id] }));

function CompanyWork() {
  return (
    <section className="scientist-company-work journey-shell" aria-labelledby="scientist-company-work-title">
      <h2 id="scientist-company-work-title">Our work in the lab</h2>
      <ul>
        {COMPANY_WORK.map(({ id, name, work, source }) => (
          <li key={id}><h3>{source ? <a href={source} target="_blank" rel="noreferrer">{name} <ArrowUpRight size={15} aria-hidden="true" /></a> : name}</h3><p>{work}</p></li>
        ))}
      </ul>
      <a className="scientist-work-source" href={PUBLIC_WORK_ARTIFACT.href} target="_blank" rel="noreferrer">{PUBLIC_WORK_ARTIFACT.label} <ArrowUpRight size={16} aria-hidden="true" /></a>
    </section>
  );
}

function WorkShowcase() {
  const [selected, setSelected] = useState(0);
  const [showMethod, setShowMethod] = useState(false);
  const tabs = useRef([]);
  const active = pillars[selected];

  const handleKeys = (event) => {
    let next;
    if (event.key === "ArrowRight") next = (selected + 1) % pillars.length;
    if (event.key === "ArrowLeft") next = (selected - 1 + pillars.length) % pillars.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = pillars.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  };

  return (
    <section className="scientist-work journey-shell" id="ai-infrastructure" aria-labelledby="scientist-work-title">
      <header className="scientist-section-heading scientist-heading-simple">
        <h2 className="journey-title" id="scientist-work-title">A cornucopia of <em>solutions.</em></h2>
      </header>

      <div className="scientist-work-tabs" role="tablist" aria-label="Explore our work" onKeyDown={handleKeys}>
        {pillars.map(({ id, tabId, title, icon: Icon }, index) => (
          <button
            type="button"
            role="tab"
            className="scientist-work-tab"
            id={`scientist-tab-${tabId}`}
            aria-selected={selected === index}
            aria-controls="scientist-work-panel"
            tabIndex={selected === index ? 0 : -1}
            ref={(element) => { tabs.current[index] = element; }}
            key={id}
            onClick={() => setSelected(index)}
          >
            <Icon aria-hidden="true" />
            <span>{title}</span>
            <span className="scientist-work-tab-number" aria-hidden="true">0{index + 1}</span>
          </button>
        ))}
      </div>

      <div id="scientist-work-panel" className="scientist-work-panel" role="tabpanel" aria-labelledby={`scientist-tab-${active.tabId}`} tabIndex={0}>
        <div className="scientist-work-intro">
          <p>{active.sentence}</p>
          <Link to={active.href}>{active.title} <ArrowUpRight aria-hidden="true" /></Link>
        </div>
        {active.id === "lab-automation" && (
          <>
            <AssayPrograms compact />
            <details className="scientist-method-details" onToggle={(event) => setShowMethod(event.currentTarget.open)}>
              <summary>How a request becomes a lab method <Plus aria-hidden="true" /></summary>
              {showMethod && <div className="scientist-method-demo"><AssayLanguageDemo compact /></div>}
            </details>
          </>
        )}
        {active.id === "scientific-data" && <ScientificDataVisual />}
        {active.id === "specialized-ai" && <AgentInfrastructureSection compact />}
      </div>
    </section>
  );
}

function IntegrationHarness({ asPage = false }) {
  const Heading = asPage ? "h1" : "h2";
  return (
    <section className={`scientist-harness${asPage ? " scientist-harness-page" : ""}`} aria-labelledby="scientist-harness-title">
      <div className="journey-shell">
        <header className="scientist-section-heading scientist-heading-simple">
          <Heading className="journey-title" id="scientist-harness-title">Let’s make it work<br /><em>in your lab.</em></Heading>
        </header>

        <figure className="scientist-harness-figure">
          <div className="scientist-harness-expert">
            <span className="scientist-expert-dot" aria-hidden="true" />
            <strong>Your scientific team</strong>
            <span>Reviews plans and results</span>
          </div>
          <div className="scientist-expert-line" aria-hidden="true"><ArrowDown /></div>
          <div className="scientist-harness-center">
            <div className="scientist-harness-brand">
              <img className="scientist-logo-light" src="/Logo.svg" alt="Cornucopia" width="152" height="42" loading="lazy" />
              <img className="scientist-logo-dark" src="/Logo-dark.svg" alt="Cornucopia" width="152" height="42" loading="lazy" />
            </div>
            <div className="scientist-harness-actions"><span>Model</span><ArrowRight aria-hidden="true" /><span>Experiment</span><ArrowRight aria-hidden="true" /><span>Learn</span></div>
            <p>Actions follow your permissions.</p>
          </div>
          <div className="scientist-harness-branches" aria-hidden="true"><i /><i /><i /></div>
          <div className="scientist-harness-systems">
            <div><Database aria-hidden="true" /><h3>Your data lake</h3></div>
            <div><BookOpen aria-hidden="true" /><h3>Your ELN</h3><p>Protocols · run records</p></div>
            <div><Cable aria-hidden="true" /><h3>Your instruments</h3><p>Methods · run status</p></div>
          </div>
          <figcaption>We confirm device versions, interface access and supported actions before scoping a pilot. <Link to={SCIENCE_LINKS.integrations.to}>{SCIENCE_LINKS.integrations.label}</Link></figcaption>
        </figure>

        <div className="scientist-ownership">
          <h2>Know who keeps it running.</h2>
          <div>
            <p>Your team owns scientific decisions and lab operation. We deliver the agreed methods and integrations, then hand them over or maintain them under a support plan.</p>
            <details className="scientist-handoff-details">
              <summary>What we hand over <Plus aria-hidden="true" /></summary>
              <p>Editable files, run records, known limits and an operator runbook. The scope names who handles software changes, hardware service and failed-run recovery, with a support window agreed before work starts.</p>
            </details>
          </div>
        </div>
      </div>
    </section>
  );
}

function PilotProcess() {
  return (
    <section className="scientist-pilot journey-shell" id="pilot" aria-labelledby="scientist-pilot-title">
      <header className="scientist-section-heading scientist-heading-simple">
        <h2 className="journey-title" id="scientist-pilot-title">A pilot with <em>a clear test.</em></h2>
      </header>
      <ol className="scientist-pilot-steps">
        <li>
          <div className="scientist-pilot-symbol"><span>01</span><SlidersHorizontal aria-hidden="true" /></div>
          <h3>Choose one workflow.</h3>
          <p>Name the method, instruments, software versions and operator. Record the current baseline.</p>
        </li>
        <li>
          <div className="scientist-pilot-symbol"><span>02</span><FlaskConical aria-hidden="true" /></div>
          <h3>Agree on what must pass.</h3>
          <div className="scientist-control-pair"><span><Plus aria-hidden="true" /> Positive</span><span><Minus aria-hidden="true" /> Negative</span></div>
          <p>Set acceptance criteria and appropriate controls. Agree on blinding where feasible and held-out cases for models.</p>
        </li>
        <li>
          <div className="scientist-pilot-symbol"><span>03</span><Cable aria-hidden="true" /></div>
          <h3>Test runs and recovery.</h3>
          <p>Run the agreed cases, including selected failures, stops and restarts. Keep method versions and source records.</p>
        </li>
        <li>
          <div className="scientist-pilot-symbol"><span>04</span><MessageCircle aria-hidden="true" /></div>
          <h3>Hand it to your team.</h3>
          <p>Your operator reruns the workflow. Review negative and inconclusive findings, known limits and support ownership before expanding.</p>
        </li>
      </ol>
      <div className="scientist-pilot-bottom">
        <Link className="journey-button" to={`${SCIENCE_LINKS.contact.to}?source=scientist-pilot`} data-cta="scientist-pilot">{SCIENCE_LINKS.contact.label} <ArrowUpRight aria-hidden="true" /></Link>
        <nav className="scientist-pilot-links" aria-label="Project details">
          <Link to={SCIENCE_LINKS.engagements.to}>{SCIENCE_LINKS.engagements.label} <ArrowUpRight aria-hidden="true" /></Link>
          <Link to={SCIENCE_LINKS.evaluation.to}>{SCIENCE_LINKS.evaluation.label} <ArrowUpRight aria-hidden="true" /></Link>
        </nav>
      </div>
      <section className="scientist-pilot-example" id="pilot-example" aria-labelledby="scientist-pilot-example-title">
        <div>
          <span>Example pilot · retrospective assay review</span>
          <h3 id="scientist-pilot-example-title">What changed between antibody assay runs?</h3>
        </div>
        <p>Start with a small set of plate maps, raw readouts, controls and protocol versions. Trace the differences, have the assay owner review the explanation, then agree on a repeat experiment. This is an example scope, not a reported result.</p>
      </section>
      <details className="journey-source-details scientist-pilot-detail">
        <summary>What we mean by a rigorous pilot</summary>
        <p>We specify success criteria, controls and evaluation before testing. Assay controls check the experiment; held-out benchmarks check model performance. We document who is blinded, and any exceptions. A feasibility pilot does not establish therapeutic efficacy.</p>
        <a href="https://www.grants.nih.gov/policy-and-compliance/policy-topics/reproducibility" target="_blank" rel="noreferrer">NIH guidance on rigor and reproducibility <ArrowUpRight aria-hidden="true" /></a>
      </details>
    </section>
  );
}

function ResearchNotes() {
  return (
    <div className="journey-shell scientist-research-notes">
      <details className="journey-source-details">
        <summary>The research behind these questions</summary>
        <ul>
          <li><a href="https://www.nature.com/articles/s41467-025-60959-z" target="_blank" rel="noreferrer">RNA delivery: cellular barriers and endosomal escape (2025)</a></li>
          <li><a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8763625/" target="_blank" rel="noreferrer">Multimodal profiling: chromatin, RNA and protein (2021)</a></li>
          <li><a href="https://www.nature.com/articles/s41467-024-46599-9" target="_blank" rel="noreferrer">Fusion design: how geometry changes bispecific function (2024)</a></li>
          <li><a href="https://www.nature.com/articles/s41587-025-02777-8" target="_blank" rel="noreferrer">Cell-state prediction: testing generalization beyond systematic bias (2025)</a></li>
          <li><a href="https://www.nature.com/articles/s41467-025-65690-3" target="_blank" rel="noreferrer">Early safety signals: human toxicogenomics and liver-injury prediction (2025)</a></li>
        </ul>
      </details>
    </div>
  );
}

export default function ScientistStory({ page = "overview" }) {
  if (page === "working-together") {
    return (
      <div className="home-editorial scientist-story scientist-working-page" data-audience="scientist">
        <IntegrationHarness asPage />
        <PilotProcess />
      </div>
    );
  }

  if (page === "open-problems") {
    return (
      <div className="home-editorial scientist-story scientist-problems-page" data-audience="scientist">
        <ProblemRequests asPage contactPath={SCIENCE_LINKS.contact.to} />
        <ResearchNotes />
      </div>
    );
  }

  if (page === "field-notes") {
    return (
      <div className="home-editorial scientist-story scientist-notes-page" data-audience="scientist">
        <FieldNotes asPage contactPath={SCIENCE_LINKS.contact.to} />
      </div>
    );
  }

  return (
    <div className="home-editorial scientist-story" data-audience="scientist">
      <section className="scientist-intro journey-shell" id="platform" aria-labelledby="scientist-intro-title">
        <div className="scientist-intro-copy">
          <h1 className="journey-title" id="scientist-intro-title">Make more of the experiments <em>you’ve already run.</em></h1>
          <p className="journey-copy">We connect your experimental records, specialized agents and lab workflows so your team can decide what to test next.</p>
          <div className="scientist-intro-links">
            <Link className="journey-button" to={`${SCIENCE_LINKS.contact.to}?source=scientist-hero`} data-cta="discuss-project">{SCIENCE_LINKS.contact.label} <ArrowUpRight aria-hidden="true" /></Link>
            <Link className="scientist-inline-link" to={SCIENCE_LINKS.agents.to}>{SCIENCE_LINKS.agents.label} <ArrowDown aria-hidden="true" /></Link>
            <Link className="scientist-inline-link" to={SCIENCE_LINKS.benchmarks.to}>{SCIENCE_LINKS.benchmarks.label} <ArrowDown aria-hidden="true" /></Link>
          </div>
        </div>
        <ResearchCompanion />
      </section>
      <CompanyWork />
      <ScientificEvidence />
      <ScientificContext />
      <LabHistory />
      <ScientistTeam />
      <ScientificEvaluation />
      <WorkShowcase />
      <div className="scientist-overview-next journey-shell">
        <Link className="journey-button" to={`${SCIENCE_LINKS.contact.to}?source=scientist-story`} data-cta="discuss-project">{SCIENCE_LINKS.contact.label} <ArrowUpRight aria-hidden="true" /></Link>
        <Link className="scientist-inline-link" to={SCIENCE_LINKS.workingTogether.to}>{SCIENCE_LINKS.workingTogether.label} <ArrowRight aria-hidden="true" /></Link>
      </div>
    </div>
  );
}
